import { ViewIdentifiable} from "../../models/common/ViewSettings";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {
    Project,
    ProjectId,
    ProjectListItem,
    ProjectModule,
} from "../../models/projects/Project";
import {ApiReturnType, getUserViewSettingsApiRequest} from "../../models/common/ApiReturnType";
import {
    sendUserToPage,
    ViewActionTypes
} from "../common/UserViewSettingsActions";
import {AppState} from "../../reducers/RootReducer";
import { ofType, StateObservable} from "redux-observable";
import {filter, switchMap, map, catchError, mergeMap } from "rxjs/operators";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {eventNotificationError, eventNotificationSuccess} from "../common/EventNotificationActions";
import { ajax } from "rxjs/ajax";
import { Observable, of } from 'rxjs';
import {Nullable} from "../../models/common/Nullable";
import {RouteConfig, RouteMethods} from "../../config/RouteConfig";
import {Identifier} from "../../models/common/Identifier";
import {ProjectFormViewState} from "../../components/projects/form/ProjectFormView";


/**
 * Constructs the API path for accessing project resources.
 *
 * @param {Nullable<number>} [id=null] The identifier for a specific project. If null, the path for general projects is returned.
 * @return {string} The constructed API path as a string.
 */
function apiPath( id: Nullable< number > = null ): string {
    if( id === null ) {
        return APP_CONFIG.baseWeb + 'projects/';
    } else {
        return `${ APP_CONFIG.baseWeb }projects/${ id }`;
    }
}

/**
 * Represents the collection of action types used in the project view context.
 *
 * Combines action types related to fetching projects, fetching a specific project by ID,
 * view actions, creating a project, updating a project, and caching project form state.
 *
 * This composite type enables consistent handling of various project-related actions
 * within the application.
 */
export type ProjectViewActionTypes = FetchProjectsActionTypes
    | FetchProjectByIdActionTypes
    | ViewActionTypes
    | CreateProjectActionTypes
    | UpdateProjectActionTypes
    | ProjectFormStateCacheActions


/**
 * Represents an action dispatched to fetch project data in a Redux application.
 * This interface extends both `ViewIdentifiable` and `ReduxAction` to ensure
 * the action contains view identification metadata and adheres to Redux action structure.
 *
 * The action type is strictly defined as `FETCH_PROJECTS` to facilitate type safety
 * and reduce runtime errors.
 *
 * @interface FetchProjectsAction
 * @extends ViewIdentifiable
 * @extends ReduxAction<typeof FETCH_PROJECTS>
 */
export interface FetchProjectsAction extends ViewIdentifiable, ReduxAction< typeof FETCH_PROJECTS > {}

/**
 * Represents a successful action when fetching a list of projects.
 *
 * This interface extends `ViewIdentifiable` for associating the action
 * with a specific view, and `ReduxAction` to define the action type.
 *
 * The `payload` contains an array of project list items returned from the API,
 * encapsulated within `ApiReturnType` to ensure consistent data handling.
 *
 * - `ViewIdentifiable` provides properties for associating the action with a view.
 * - `ReduxAction` ensures that the action conforms to the Redux action structure and specifies the action type as `FETCH_PROJECTS_SUCCESS`.
 * - `ApiReturnType<ProjectListItem[]>` represents the structure of the API response, specifically an array of project list items.
 */
export interface FetchProjectsSuccessAction extends ViewIdentifiable, ReduxAction<typeof FETCH_PROJECTS_SUCCESS > {
    payload: ApiReturnType< ProjectListItem[] >
}

/**
 * Represents an action dispatched when there is an error during the fetching of projects.
 * This action extends functionality from both `ViewIdentifiable` and `ErrorAction`.
 *
 * It is associated with the `FETCH_PROJECTS_ERROR` action type and provides information
 * about the error encountered while attempting to fetch project data.
 */
export interface FetchProjectsErrorAction extends ViewIdentifiable, ErrorAction< typeof FETCH_PROJECTS_ERROR >{}

/**
 * Represents the action types related to fetching projects.
 *
 * This type encompasses all possible actions that can occur in the
 * process of fetching project data, including the initial fetch action,
 * error handling, and success handling.
 *
 * - `FetchProjectsAction`: Represents the initiation of a project fetch request.
 * - `FetchProjectsErrorAction`: Represents an error encountered during the fetch process.
 * - `FetchProjectsSuccessAction`: Represents a successful response containing project data.
 *
 * Used to strongly type the actions in the project fetching workflow.
 */
export type FetchProjectsActionTypes = FetchProjectsAction | FetchProjectsErrorAction | FetchProjectsSuccessAction

/**
 * A constant representing the action type for fetching projects.
 * This is typically used in state management or redux-based implementations to identify
 * the action dispatched for retrieving project data.
 */
export const FETCH_PROJECTS = "FETCH_PROJECTS";
/**
 * A constant that represents the successful completion of a project fetch operation.
 * It is used as an action type in the application's state management flow, typically
 * within a Redux-based architecture, to signal that project data has been retrieved successfully.
 */
export const FETCH_PROJECTS_SUCCESS = "FETCH_PROJECTS_SUCCESS";
/**
 * Represents the error action type dispatched when fetching projects fails.
 * This constant is used in the application's state management to identify
 * and handle errors that occur during the project fetch process.
 */
export const FETCH_PROJECTS_ERROR = "FETCH_PROJECTS_ERROR";

/**
 * Fetches a list of projects based on the provided view identifier.
 *
 * @param {string} viewIdentifier - A unique identifier for the view context to fetch projects for.
 * @return {FetchProjectsAction} An action object containing the type and viewIdentifier.
 */
export function fetchProjects( viewIdentifier: string ) : FetchProjectsAction {
    return { type: FETCH_PROJECTS, viewIdentifier };
}

/**
 * Creates an action to indicate the successful fetching of project data.
 *
 * @param {string} viewIdentifier - A unique identifier for the view requesting the projects.
 * @param {ApiReturnType<ProjectListItem[]>} payload - The payload containing the fetched project data.
 * @return {FetchProjectsSuccessAction} The action object containing the type, view identifier, and payload.
 */
export function fetchProjectsSuccess( viewIdentifier: string, payload: ApiReturnType< ProjectListItem[] >) : FetchProjectsSuccessAction {
    return { type: FETCH_PROJECTS_SUCCESS, viewIdentifier, payload };
}

/**
 * Creates an action to represent an error occurring while fetching projects.
 *
 * @param {string} viewIdentifier - A unique identifier for the view where the error occurred.
 * @param {Error} error - The error object that provides details about the failure.
 * @return {FetchProjectsErrorAction} An action object with the type `FETCH_PROJECTS_ERROR`,
 * containing the view identifier and the error.
 */
export function fetchProjectsError( viewIdentifier: string, error: Error ): FetchProjectsErrorAction {
    return { type: FETCH_PROJECTS_ERROR, viewIdentifier, error };
}

/**
 * Epic to handle fetching projects based on user interactions.
 *
 * @param {Observable<FetchProjectsActionTypes>} action$ - Stream of actions observed in the application.
 * @param {StateObservable<AppState>} store$ - Observable of the current application state.
 * @return {Observable} A stream of actions, which may include success or error handling actions.
 */
export function fetchProjectsEpic(
    action$: Observable< FetchProjectsActionTypes >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(FETCH_PROJECTS),
        filter(action => action.viewIdentifier === ProjectModule.identifier),
        switchMap(action => {
            const request = getUserViewSettingsApiRequest(
                apiPath(),
                ProjectModule.identifier,
                store$
            );

            return ajax(APP_CONFIG.getRequestObject(request.path)).pipe(
                map(response => fetchProjectsSuccess(
                    action.viewIdentifier,
                    response.response as ApiReturnType< ProjectListItem[] >
                )),
                catchError(error => of(
                    fetchProjectsError(action.viewIdentifier, error),
                    eventNotificationError(error, store$, ProjectModule.identifier, "fetchProjects")
                ))
            );

        })
    );
}


/**
 * fetch a single project for detail
 */
export interface FetchProjectByIdAction extends ProjectId, ReduxAction<typeof FETCH_PROJECT_BY_ID> {}
export interface FetchProjectByIdSuccessAction extends ProjectId, ReduxAction<typeof FETCH_PROJECT_BY_ID_SUCCESS> {
    payload: Project
}
export interface FetchProjectByIdErrorAction extends ProjectId, ErrorAction<typeof FETCH_PROJECT_BY_ID_ERROR> {}

export type FetchProjectByIdActionTypes = FetchProjectByIdAction | FetchProjectByIdSuccessAction | FetchProjectByIdErrorAction

// redux action constants
export const FETCH_PROJECT_BY_ID = "FETCH_PROJECT_BY_ID";
export const FETCH_PROJECT_BY_ID_SUCCESS = "FETCH_PROJECT_BY_ID_SUCCESS";
export const FETCH_PROJECT_BY_ID_ERROR = "FETCH_PROJECT_BY_ID_ERROR";

//redux action functions
export function fetchProjectById( projectId: Identifier<number> ): FetchProjectByIdAction {
    return { type: FETCH_PROJECT_BY_ID, projectId };
}
export function fetchProjectByIdSuccess( projectId: Identifier<number>, payload: Project ): FetchProjectByIdSuccessAction {
    return { type: FETCH_PROJECT_BY_ID_SUCCESS, projectId, payload };
}
export function fetchProjectByIdError( projectId: Identifier<number>, error: any ): FetchProjectByIdErrorAction {
    return { type: FETCH_PROJECT_BY_ID_ERROR, projectId, error };
}

//redux observable epic
export function fetchProjectByIdEpic(
    action$: Observable< FetchProjectByIdAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_PROJECT_BY_ID ),
        mergeMap( action => {
            const path = apiPath( action.projectId.rawValue );

            return ajax( {
               ...APP_CONFIG.getRequestObject( path ),
                timeout: 8000
            } ).pipe(
                map( response => fetchProjectByIdSuccess(
                    action.projectId,
                    response.response as Project
                ) ),
                catchError( error => of(
                    fetchProjectByIdError( action.projectId, error ),
                    eventNotificationError( error, store$, ProjectModule.identifier, "fetchProjectById" )
                ) )
            )
        } )
    );
}

/**
 * create a new project action
 */
export interface CreateProjectAction extends ReduxAction<typeof CREATE_PROJECT> {
    project: Project
}
export interface CreateProjectSuccessAction extends ReduxAction<typeof CREATE_PROJECT_SUCCESS> {
    project: Project
}
export interface CreateProjectErrorAction extends ErrorAction<typeof CREATE_PROJECT_ERROR> {
    project: Project
}

export type CreateProjectActionTypes = CreateProjectAction | CreateProjectSuccessAction | CreateProjectErrorAction

/**
 * create project redux action constants
 */
export const CREATE_PROJECT = "CREATE_PROJECT";
export const CREATE_PROJECT_SUCCESS = "CREATE_PROJECT_SUCCESS";
export const CREATE_PROJECT_ERROR = "CREATE_PROJECT_ERROR";

/**
 * CREATE PROJECT redux action function
 */
export function createProject( project: Project ): CreateProjectAction {
    return {  type: CREATE_PROJECT, project };
}
export function createProjectSuccess( project: Project ): CreateProjectSuccessAction {
    return { type: CREATE_PROJECT_SUCCESS, project };
}
export function createProjectError( project: Project, error: any ): CreateProjectErrorAction {
    return { type: CREATE_PROJECT_ERROR, project, error };
}

/**
 * create project redux observable epic
 */
export function createProjectEpic(
    action$: Observable< CreateProjectAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( CREATE_PROJECT ),
        switchMap( action => {
            return ajax( APP_CONFIG.getRequestObject(
                apiPath(),
                RouteMethods.post,
                action.project
            ) ).pipe(
                mergeMap( _ => of(
                    createProjectSuccess( action.project ),
                    eventNotificationSuccess(),
                    sendUserToPage( RouteConfig.project.routes.main.path, ProjectModule.identifier )
                ) ),
                catchError( error => {

                    return of(
                        createProjectError( action.project, error ),
                        eventNotificationError( error, store$, 'projects', 'createProject' )
                    )
                } )
            );
        } )
    )
}


/**
 * cache project form
 * redux cache for the project creation/edit form.
 */
export interface ProjectFormStateToCacheAction extends ReduxAction<typeof PROJECT_FORM_STATE_TO_CACHE> {
    formState: ProjectFormViewState
}
export interface ProjectFormStateResetCacheAction extends ReduxAction<typeof PROJECT_FORM_STATE_CACHE_RESET> {
}
export type ProjectFormStateCacheActions = ProjectFormStateResetCacheAction | ProjectFormStateToCacheAction

export const PROJECT_FORM_STATE_TO_CACHE = "PROJECT_FORM_STATE_TO_CACHE";
export const PROJECT_FORM_STATE_CACHE_RESET = "PROJECT_FORM_STATE_CACHE_RESET";

export function cacheProjectFormState( state: ProjectFormViewState ): ProjectFormStateToCacheAction {
    return { type: PROJECT_FORM_STATE_TO_CACHE, formState: state };
}
export function resetProjectFormStateCache(): ProjectFormStateResetCacheAction {
    return { type: PROJECT_FORM_STATE_CACHE_RESET };
}


/**
 * create a new project action
 */
export interface UpdateProjectAction extends ReduxAction<typeof UPDATE_PROJECT> {
    project: Project
}
export interface UpdateProjectSuccessAction extends ReduxAction<typeof UPDATE_PROJECT_SUCCESS> {
    project: Project
}
export interface UpdateProjectErrorAction extends ErrorAction<typeof UPDATE_PROJECT_ERROR> {
    project: Project
}

export type UpdateProjectActionTypes = UpdateProjectAction | UpdateProjectSuccessAction | UpdateProjectErrorAction

/**
 * create project redux action constants
 */
export const UPDATE_PROJECT = "UPDATE_PROJECT";
export const UPDATE_PROJECT_SUCCESS = "UPDATE_PROJECT_SUCCESS";
export const UPDATE_PROJECT_ERROR = "UPDATE_PROJECT_ERROR";

/**
 * CREATE PROJECT redux action function
 */
export function updateProject( project: Project ): UpdateProjectAction {
    return {  type: UPDATE_PROJECT, project };
}
export function updateProjectSuccess( project: Project ): UpdateProjectSuccessAction {
    return { type: UPDATE_PROJECT_SUCCESS, project };
}
export function updateProjectError( project: Project, error: any ): UpdateProjectErrorAction {
    return { type: UPDATE_PROJECT_ERROR, project, error };
}

/**
 * create project redux observable epic
 */
export function updateProjectEpic(
    action$: Observable< UpdateProjectAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( UPDATE_PROJECT ),
        switchMap( action => {

            return ajax( APP_CONFIG.getRequestObject(
                apiPath( action.project.projectId.rawValue ),
                RouteMethods.put,
                action.project
            ) ).pipe(
                mergeMap( _ => of(
                    updateProjectSuccess( action.project ),
                    eventNotificationSuccess(),
                    sendUserToPage( RouteConfig.project.routes.main.path, ProjectModule.identifier )
                ) ),
                catchError( error => {

                    return of(
                        updateProjectError( action.project, error ),
                        eventNotificationError( error, store$, 'projects', 'updateProject' )
                    )
                } )
            );
        } )
    )
}